import React from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  Chip,
} from '@mui/material';
import { IconListDetails } from '@tabler/icons-react';
import { DateField } from '../dropdown';
import type {
  AdditionalField,
  AdditionalDetailsValues,
  AdditionalFieldOption,
  TemplateCustomerContext,
  FieldStrictness,
} from '../../types/ticketTypeTemplate';
import { shouldShowField, applyDropdownPriority } from '../../utils/templateEngine';
import { countUsernameLines } from '../../utils/assignLicenseBilling';
import { LicensePicker } from './LicensePicker';
import type { LicenseTerm } from './LicensePicker';

function isRequiredForUI(required: FieldStrictness): boolean {
  return required === 'strict';
}

/** Marks a `deriveCountFrom` field as manually overridden (override then wins). */
function overrideKey(fieldKey: string): string {
  return `__${fieldKey}_countOverridden`;
}

interface AdditionalDetailsRendererProps {
  fields: AdditionalField[];
  values: AdditionalDetailsValues;
  onChange: (values: AdditionalDetailsValues) => void;
  customerContext?: TemplateCustomerContext;
  errors?: Record<string, string>;
}

export const AdditionalDetailsRenderer: React.FC<AdditionalDetailsRendererProps> = ({
  fields,
  values,
  onChange,
  customerContext = {},
  errors = {},
}) => {
  if (!fields || fields.length === 0) {
    return null;
  }

  const handleFieldChange = (fieldKey: string, value: string | number) => {
    const next: AdditionalDetailsValues = { ...values, [fieldKey]: value };

    // Live count-derivation: when the source field of a `deriveCountFrom` number
    // field changes, recompute the count — unless the tech has overridden it.
    const derived = fields.find((f) => f.deriveCountFrom === fieldKey);
    if (derived && !values[overrideKey(derived.fieldKey)]) {
      next[derived.fieldKey] = countUsernameLines(value);
    }

    // Editing a derived-count field directly pins it (override wins from now on).
    const changedField = fields.find((f) => f.fieldKey === fieldKey);
    if (changedField?.deriveCountFrom) {
      next[overrideKey(fieldKey)] = 1;
    }

    onChange(next);
  };

  const renderField = (field: AdditionalField) => {
    const value = values[field.fieldKey] ?? field.defaultValue ?? '';
    const error = errors[field.fieldKey];

    if (!shouldShowField(field, values, customerContext)) {
      return null;
    }

    switch (field.type) {
      case 'text':
        return (
          <TextField
            key={field.fieldKey}
            fullWidth
            label={field.label}
            placeholder={field.placeholder}
            variant="outlined"
            size="small"
            required={isRequiredForUI(field.required)}
            value={value}
            onChange={(e) => handleFieldChange(field.fieldKey, e.target.value)}
            error={!!error}
            helperText={error}
            slotProps={{
              input: {
                'aria-label': field.label,
                'aria-required': isRequiredForUI(field.required),
              }
            }}
          />
        );

      case 'textarea':
        return (
          <TextField
            key={field.fieldKey}
            fullWidth
            multiline
            minRows={3}
            label={field.label}
            placeholder={field.placeholder}
            variant="outlined"
            size="small"
            required={isRequiredForUI(field.required)}
            value={value}
            onChange={(e) => handleFieldChange(field.fieldKey, e.target.value)}
            error={!!error}
            helperText={error}
            slotProps={{
              input: {
                'aria-label': field.label,
                'aria-required': isRequiredForUI(field.required),
              }
            }}
          />
        );

      case 'dropdown': {
        const prioritizedOptions = field.optionPriorityResolver
          ? applyDropdownPriority(field, values, customerContext)
          : (field.options || []);

        return (
          <TextField
            key={field.fieldKey}
            fullWidth
            select
            label={field.label}
            variant="outlined"
            size="small"
            required={isRequiredForUI(field.required)}
            value={value}
            onChange={(e) => handleFieldChange(field.fieldKey, e.target.value)}
            error={!!error}
            helperText={error}
            slotProps={{
              input: {
                'aria-label': field.label,
                'aria-required': isRequiredForUI(field.required),
              }
            }}
          >
            {prioritizedOptions.map((option: AdditionalFieldOption) => (
              <MenuItem key={option.value} value={option.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography sx={{ fontSize: 'inherit' }}>{option.label}</Typography>
                  {option.recommended && (
                    <Chip label="Recommended" size="small" color="primary" />
                  )}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        );
      }

      case 'number':
        return (
          <TextField
            key={field.fieldKey}
            fullWidth
            type="number"
            label={field.label}
            placeholder={field.placeholder}
            variant="outlined"
            size="small"
            required={isRequiredForUI(field.required)}
            value={value}
            onChange={(e) => handleFieldChange(field.fieldKey, parseFloat(e.target.value) || 0)}
            error={!!error}
            helperText={error}
            slotProps={{
              input: {
                'aria-label': field.label,
                'aria-required': isRequiredForUI(field.required),
              },
              htmlInput: {
                min: 0,
              }
            }}
          />
        );

      case 'date':
        return (
          <DateField
            key={field.fieldKey}
            label={field.label}
            value={value || null}
            onChange={(iso) => handleFieldChange(field.fieldKey, iso ?? '')}
            error={!!error}
            helperText={error}
          />
        );

      case 'license-picker':
        return (
          <LicensePicker
            key={field.fieldKey}
            label={field.label}
            placeholder={field.placeholder}
            required={isRequiredForUI(field.required)}
            catalogCategory={field.licensePicker?.catalogCategory ?? 'licensing-m365'}
            mode={field.licensePicker?.mode ?? 'all'}
            value={String(value)}
            onChange={(sku) => handleFieldChange(field.fieldKey, sku)}
            onTermChange={(term: LicenseTerm) => handleFieldChange(`${field.fieldKey}_term`, term)}
            customerContext={customerContext}
            error={error}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Section header — matches the customer-panel section bands. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          p: '10px 16px',
          mb: 2,
          borderTop: '0.5px solid #e2eaf3',
          bgcolor: '#f0f3f7',
        }}
      >
        <IconListDetails size={18} stroke={1.5} style={{ color: '#aaaaaa', flexShrink: 0 }} />
        <Box
          component="span"
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: '#646464',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
        >
          Additional Details
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {fields.map(renderField)}
      </Box>
    </Box>
  );
};

/**
 * Rules between fields, as opposed to "is it filled in" — today a date that
 * must not be earlier than another (`minDateField`). Shown inline on the field
 * as well as blocking Save: unlike a missing required field, the tech has
 * typed something and needs to see which one is wrong. Only checked once both
 * dates are set, and skipped for a hidden field.
 */
export function validateFieldRules(
  fields: AdditionalField[],
  values: AdditionalDetailsValues,
  customerContext?: TemplateCustomerContext
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    if (field.type !== 'date' || !field.minDateField) continue;
    if (field.showWhen && !shouldShowField(field, values, customerContext || {})) continue;
    const value = values[field.fieldKey];
    const min = values[field.minDateField];
    // ISO yyyy-MM-dd compares correctly as a string.
    if (typeof value === 'string' && value && typeof min === 'string' && min && value < min) {
      const minLabel = fields.find((f) => f.fieldKey === field.minDateField)?.label ?? field.minDateField;
      errors[field.fieldKey] = `${field.label} must be on or after ${minLabel}`;
    }
  }
  return errors;
}

export function validateAdditionalDetails(
  fields: AdditionalField[],
  values: AdditionalDetailsValues,
  customerContext?: TemplateCustomerContext
): Record<string, string> {
  const errors: Record<string, string> = {};

  fields.forEach((field) => {
    if (field.required === 'strict') {
      if (field.showWhen && !shouldShowField(field, values, customerContext || {})) {
        return;
      }
      const value = values[field.fieldKey];
      if (value === undefined || value === null || value === '') {
        errors[field.fieldKey] = `${field.label} is required`;
      }
    }
  });

  for (const [fieldKey, message] of Object.entries(validateFieldRules(fields, values, customerContext))) {
    if (!errors[fieldKey]) errors[fieldKey] = message;
  }

  return errors;
}
